import { useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExternalLinkAlt,
  faCog,
  faBell,
  faCheck,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { motion } from "framer-motion";

import { useNotifications } from "../../hooks/notifications/useNotifications";
import { useMarkAllNotificationsAsRead } from "../../hooks/notifications/useNotificationMutations";
import { useNotificationQueryContext } from "../../context/NotificationQueryContext";
import { useSharedEventBridgeNotifications } from "../../hooks/notifications/useSharedEventBridgeNotifications";
import { ThreadNotificationHandler } from "../../utils/notification/threadNotificationHandler";
import { useQueryClient } from "react-query";
import NotificationTypeIndicator from "./NotificationTypeIndicator";
import {
  formatRelativeTime,
  formatFullTimestamp,
} from "../../utils/notification/notificationHelpers";

import useAccessibility from "../../hooks/notifications/useAccessibility";

/**
 * NotificationDropdown Component
 *
 * Dropdown panel showing recent notifications with quick actions
 * Includes proper positioning, loading states, and accessibility
 */
const NotificationDropdown = ({
  isOpen,
  onClose,
  showActions = true,
  showQuickFilters = false,
  position = "right",
}) => {
  const { updateFilters, filters } = useNotificationQueryContext();
  const queryClient = useQueryClient();

  // Use React Query for API notifications with proper refetching
  const {
    data: notificationsData,
    isLoading,
    refetch,
  } = useNotifications({
    page: 1,
    limit: 10,
  });
  const markAllAsReadMutation = useMarkAllNotificationsAsRead();

  // Use EventBridge notifications for real-time updates
  const { notifications: eventBridgeNotifications, isConnected } =
    useSharedEventBridgeNotifications();

  // Invalidate React Query cache when new WebSocket notifications arrive
  useEffect(() => {
    if (isConnected && eventBridgeNotifications.length > 0) {
      // Invalidate cache to ensure API queries are updated
      queryClient.invalidateQueries("notifications");
      queryClient.invalidateQueries("unreadCount");
    }
  }, [eventBridgeNotifications.length, isConnected, queryClient]);

  // Memoize API notifications to prevent infinite re-renders
  const apiNotifications = useMemo(() => {
    return notificationsData?.data?.notifications || [];
  }, [notificationsData?.data?.notifications]);

  // Improved notification logic: Combine WebSocket and API notifications
  const notifications = useMemo(() => {
    // Always prefer API notifications when available (they're authoritative)
    // But supplement with WebSocket notifications for real-time updates
    if (apiNotifications.length > 0) {
      return apiNotifications.slice(0, 10);
    } else if (isConnected && eventBridgeNotifications.length > 0) {
      // Use EventBridge notifications as fallback when API data isn't available
      return eventBridgeNotifications.slice(0, 10);
    } else {
      return [];
    }
  }, [apiNotifications, isConnected, eventBridgeNotifications]);

  // Simplified unread count calculation
  const unreadCount = useMemo(() => {
    return notifications.filter((n) => {
      // Handle EventBridge notifications
      if (n.type === "notification" && n.data) {
        return !n.data.read;
      }
      // Handle API notifications
      return !n.read;
    }).length;
  }, [notifications]);

  const dropdownRef = useRef(null);

  // Use accessibility hook
  const {
    getAccessibleProps,
    announceNotificationChange,
    handleKeyboardNavigation,
    focusFirst,
    updateFocusableElements,
  } = useAccessibility({
    announceChanges: true,
    manageFocus: true,
    enableKeyboardNavigation: true,
  });

  // Refetch notifications when dropdown is opened
  useEffect(() => {
    if (isOpen) {
      // Force refetch both API and clear local state
      refetch();
      // Also invalidate cache to ensure fresh data
      queryClient.invalidateQueries("notifications");
      queryClient.invalidateQueries("unreadCount");
    }
  }, [isOpen, refetch, queryClient]);

  // Focus management
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      // Update focusable elements and focus first
      updateFocusableElements(dropdownRef.current);
      focusFirst(dropdownRef.current);
    }
  }, [isOpen, updateFocusableElements, focusFirst]);

  // Handle mark all as read
  const handleMarkAllAsRead = async () => {
    if (unreadCount > 0) {
      try {
        // Mark API notifications as read
        await markAllAsReadMutation.mutateAsync();

        // Invalidate and refetch notifications
        queryClient.invalidateQueries("notifications");
        queryClient.invalidateQueries("unreadCount");

        // Refetch to get latest data
        refetch();

        announceNotificationChange("marked-read", { count: unreadCount });
      } catch (error) {
        console.warn("Failed to mark all notifications as read:", error);
      }
    }
  };

  // Refetch notifications when dropdown is opened
  useEffect(() => {
    if (isOpen) {
      refetch();
    }
  }, [isOpen, refetch]);

  // Handle keyboard navigation
  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      onClose();
    } else {
      handleKeyboardNavigation(event, dropdownRef.current);
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      ref={dropdownRef}
      initial={{ opacity: 0, y: -10, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -10, scale: 0.95 }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 30,
        duration: 0.2,
      }}
      className={`notification-dropdown absolute z-50 mt-2 bg-gray-800 rounded-2xl shadow-header border border-gray-600
        ${position === "right" ? "right-0" : "left-0"}
        w-80 max-w-[calc(100vw-2rem)] max-h-[32rem] overflow-hidden text-white`}
      onKeyDown={handleKeyDown}
      {...getAccessibleProps("notification-dropdown")}
    >
      {/* Enhanced Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-600">
        <div className="flex items-center gap-3">
          <h3 className="text-base font-semibold text-white">Notifications</h3>
          {unreadCount > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-deep-red text-white animate-in zoom-in-50 duration-200">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </div>

        <div className="flex items-center gap-1">
          {/* View All Notifications button */}
          {showActions && (
            <Link
              to="/notifications"
              onClick={onClose}
              className="p-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors duration-200"
              title="View All Notifications"
              aria-label="View All Notifications"
            >
              <FontAwesomeIcon icon={faExternalLinkAlt} className="text-sm" />
            </Link>
          )}

          {/* Settings button */}
          {showActions && (
            <Link
              to="/notifications/preferences"
              onClick={onClose}
              className="p-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors duration-200"
              title="Notification Settings"
              aria-label="Notification Settings"
            >
              <FontAwesomeIcon icon={faCog} className="text-sm" />
            </Link>
          )}

          {/* Mark all as read button */}
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              disabled={markAllAsReadMutation.isLoading}
              className="p-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors duration-200"
              title="Mark all as read"
              aria-label="Mark all as read"
            >
              <FontAwesomeIcon icon={faCheck} className="text-sm" />
            </button>
          )}

          {/* Close button */}
          <button
            onClick={onClose}
            className="p-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors duration-200"
            title="Close notifications"
            aria-label="Close notifications"
          >
            <FontAwesomeIcon icon={faTimes} className="text-sm" />
          </button>
        </div>
      </div>

      {/* Status indicator */}
      {unreadCount === 0 && notifications.length > 0 && !isLoading && (
        <div className="px-4 py-2 bg-green-800 border-b border-green-700">
          <p className="text-xs text-green-200 text-center font-medium">
            All caught up! No new notifications.
          </p>
        </div>
      )}

      {/* Quick Filters */}
      {showQuickFilters && notifications.length > 0 && (
        <div className="p-4 border-b border-gray-100">
          <div className="segmented">
            <button
              onClick={() => updateFilters({ read: null })}
              className={`segmented__btn ${
                filters.read === null ? "is-active bg-deep-red text-white" : ""
              }`}
            >
              All
            </button>
            <button
              onClick={() => updateFilters({ read: false })}
              className={`segmented__btn ${
                filters.read === false ? "is-active bg-deep-red text-white" : ""
              }`}
            >
              Unread
            </button>
            <button
              onClick={() => updateFilters({ read: true })}
              className={`segmented__btn ${
                filters.read === true ? "is-active bg-deep-red text-white" : ""
              }`}
            >
              Read
            </button>
          </div>
        </div>
      )}

      {/* Notification List */}
      <div className="notification-list overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent max-h-96">
        {isLoading ? (
          // Enhanced Loading state
          <div className="p-4">
            <div className="space-y-3">
              {[...Array(4)].map((_, index) => (
                <div
                  key={index}
                  className="animate-pulse p-4 bg-gray-700 border border-gray-600 rounded-xl"
                >
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 bg-gray-600 rounded-full"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-600 rounded w-3/4"></div>
                      <div className="h-3 bg-gray-600 rounded w-1/2"></div>
                      <div className="h-2 bg-gray-600 rounded w-1/3"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : notifications.length === 0 ? (
          // Enhanced Empty state
          <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
            <div className="w-20 h-20 bg-gray-700 rounded-full flex items-center justify-center mb-4">
              <FontAwesomeIcon
                icon={faBell}
                className="text-3xl text-gray-400"
              />
            </div>
            <h4 className="text-base font-semibold text-white mb-2">
              No notifications
            </h4>
            <p className="text-sm text-gray-300">You're all caught up!</p>
          </div>
        ) : (
          // Enhanced Notification items with animations
          <div className="space-y-0 p-1">
            {notifications.map((notification, index) => {
              // Handle both API notifications and EventBridge notifications
              const isEventBridge =
                notification.type === "notification" && notification.data;
              const notificationData = isEventBridge
                ? notification.data
                : notification;
              const isThreadNotification =
                ThreadNotificationHandler.isThreadNotification({
                  type: notificationData.type,
                });

              // Get display information
              let displayInfo;
              let isFighterCancellation = false;

              if (isThreadNotification) {
                displayInfo =
                  ThreadNotificationHandler.getThreadNotificationText({
                    type: notificationData.type,
                    data: notificationData,
                  });
              } else if (notificationData.type === "fighter_cancelled") {
                // Handle fighter cancellation notifications
                isFighterCancellation = true;
                const { fighterName, eventName } =
                  notificationData.metadata || {};
                displayInfo = {
                  title: "Fight Cancelled",
                  message: `${fighterName}'s fight has been cancelled for ${eventName}. They will score 0 points.`,
                  preview: null,
                };
              } else {
                displayInfo = {
                  message:
                    notificationData.type === "follow"
                      ? `${
                          notificationData.actor?.name ||
                          notificationData.metadata?.actorName ||
                          "Someone"
                        } started following you`
                      : `${
                          notificationData.actor?.name ||
                          notificationData.metadata?.actorName ||
                          "Someone"
                        } unfollowed you`,
                  preview: null,
                };
              }

              const handleNotificationClick = () => {
                // Note: Read status will be handled by the backend/API
                // For now, we just navigate and let the system handle read status

                if (isThreadNotification) {
                  // Handle thread notification navigation
                  const threadId =
                    notificationData.threadId ||
                    notificationData.metadata?.threadId;
                  const messageId =
                    notificationData.messageId ||
                    notificationData.metadata?.messageId;
                  const replyId =
                    notificationData.replyId ||
                    notificationData.metadata?.replyId;
                  const fightId =
                    notificationData.fightId ||
                    notificationData.metadata?.fightId;

                  if (threadId) {
                    let path = `/threads/${threadId}`;
                    const params = new URLSearchParams();
                    if (messageId) params.append("messageId", messageId);
                    if (replyId) params.append("replyId", replyId);
                    if (fightId) params.append("fightId", fightId);
                    if (params.toString()) path += `?${params.toString()}`;

                    window.location.href = path;
                  }
                } else if (isFighterCancellation) {
                  // Handle fighter cancellation navigation
                  const eventId = notificationData.metadata?.eventId;
                  if (eventId) {
                    window.location.href = `/events/${eventId}/lineups`;
                  } else {
                    window.location.href = "/events";
                  }
                }

                onClose();
              };

              return (
                <motion.div
                  key={notificationData._id || notification.messageId || index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    delay: index * 0.05,
                    duration: 0.2,
                    ease: "easeOut",
                  }}
                  className={`group relative p-4 hover:bg-gray-600 rounded-xl transition-colors cursor-pointer border-b border-gray-600 last:border-b-0
                    ${
                      !notificationData.read
                        ? isFighterCancellation
                          ? "bg-orange-900/20 shadow-sm border-l-4 border-l-orange-500"
                          : "bg-gray-700 shadow-sm"
                        : "bg-gray-800"
                    }
                  `}
                  onClick={handleNotificationClick}
                >
                  <div className="flex items-start gap-3">
                    {/* Avatar or Warning Icon */}
                    {isFighterCancellation ? (
                      // Warning icon for fighter cancellations
                      <div className="flex-shrink-0 w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center ring-2 ring-orange-400">
                        <span
                          className="text-orange-600 text-xl"
                          role="img"
                          aria-label="Warning"
                        >
                          ⚠️
                        </span>
                      </div>
                    ) : (
                      // Type indicator dot for other notifications
                      <div className="mt-1">
                        <NotificationTypeIndicator
                          type={notificationData.type}
                          isRead={notificationData.read}
                        />
                      </div>
                    )}

                    {/* Notification content */}
                    <div className="flex-1 min-w-0 space-y-1">
                      {isFighterCancellation ? (
                        // Fighter cancellation specific layout
                        <>
                          <p className="text-sm font-bold text-orange-400">
                            {displayInfo.title}
                            {!notificationData.read && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-600 text-white">
                                NEW
                              </span>
                            )}
                          </p>
                          <p className="text-sm text-white font-medium">
                            {displayInfo.message}
                          </p>
                        </>
                      ) : (
                        // Regular notification layout
                        <div className="flex items-start justify-between gap-2">
                          <p
                            className="text-sm font-bold line-clamp-1 text-white"
                            style={{ color: "#ffffff" }}
                          >
                            {displayInfo.message}
                            {!notificationData.read && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-deep-red text-white">
                                NEW
                              </span>
                            )}
                          </p>
                        </div>
                      )}

                      {/* Thread context for thread notifications */}
                      {isThreadNotification &&
                        notificationData.metadata?.threadTitle && (
                          <p
                            className="text-sm text-gray-300 italic font-bold"
                            style={{ color: "#d1d5db" }}
                          >
                            in "{notificationData.metadata.threadTitle}"
                          </p>
                        )}

                      {/* Message preview for thread notifications */}
                      {displayInfo.preview && (
                        <p
                          className="text-sm text-gray-200 bg-gray-600 rounded px-2 py-1 mt-1 border border-gray-500 font-bold"
                          style={{ color: "#e5e7eb" }}
                        >
                          "{displayInfo.preview}"
                        </p>
                      )}

                      {notificationData.message && !isThreadNotification && (
                        <p
                          className="text-sm line-clamp-2 mt-1 font-bold text-white"
                          style={{ color: "#ffffff" }}
                        >
                          {notificationData.message}
                        </p>
                      )}

                      <p
                        className="text-sm mt-2 font-bold text-gray-300"
                        style={{ color: "#d1d5db" }}
                        title={formatFullTimestamp(notificationData.createdAt)}
                      >
                        {formatRelativeTime(notificationData.createdAt)}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default NotificationDropdown;
